import Typography from "@mui/material/Typography";
import { useTransactions } from "api/transactions";
import { useAppSelector } from "app/hooks";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useNavigate, useParams } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { USER_TRANSACTIONS_LIST_ID } from "state/tables/types";
import { ListingActions, ListResponse } from "types/listing";
import { TransactionBackendModel } from "types/transactions";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatCurrencyValue } from "utilities/numbers";
import { capitalizeFirstLetter } from "utilities/strings";
import { useStyles } from "./SingleMember.styles";

export const getTransactionMetric = (
  transactionData: TransactionBackendModel | undefined,
  metricName: string,
) => transactionData?.metrics?.find((metric) => metric.name === metricName);

export function Transactions() {
  const { id: memberID } = useParams<{ id: string; page: string }>();
  const classes = useStyles();
  const { message } = useMessage();
  const { listing, updateListing } = useListing();
  const navigate = useNavigate();
  const { setColumnOrdering } = useTablesStateHelpers();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const { useTransactionsList } = useTransactions();
  const { data: membersTransactions } = useTransactionsList({
    filters: memberID
      ? [
          {
            condition: "eq",
            query: "customerId",
            value: memberID,
          },
        ]
      : undefined,
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "header:purchasedAt",
  }) as { data: ListResponse };

  const getTransactionLabel = (type: "sell" | "return") => {
    switch (type) {
      case "sell":
        return message({ id: "purchase" });
      case "return":
        return message({ id: "return" });
      default:
        return capitalizeFirstLetter(type);
    }
  };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "documentNumber" }),
      property: "header:documentNumber",
      isSortable: true,
    },
    {
      headerText: message({ id: "documentType" }),
      property: "header:documentType",
      isSortable: true,
    },
    {
      headerText: message({ id: "documentDate" }),
      property: "header:purchasedAt",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "transactionValueWithCurrency" }, { currency }),
      property: "grossValue",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "channel" }),
      property: "channel",
    },
    {
      align: "right",
      headerText: message({ id: "pointsEarned" }),
      property: "pointsEarned",
      isVisible: false,
    },
  ]);

  const items = membersTransactions?.items
    ? createItems(membersTransactions.items)
    : [];

  function createItems(items: any) {
    return items.map((item: any) => {
      const fraudProbabilityValue = Number(
        getTransactionMetric(item, "fraud_probability")?.value,
      );

      return {
        id: item.transactionId,
        cells: [
          {
            content: item.header?.documentNumber,
            property: "header:documentNumber",
          },
          {
            content: getTransactionLabel(item.header?.documentType),
            property: "header:documentType",
          },
          {
            content: OLDate(item.header?.purchasedAt, {
              formatString: "yyyy-MM-dd",
            }),
            property: "header:purchasedAt",
          },
          {
            align: "right",
            content: formatCurrencyValue(item.grossValue),
            property: "grossValue",
          },
          {
            align: "right",
            content: item?.channelName || "-",
            property: "channel",
          },
          {
            align: "right",
            content: item.pointsEarned,
            emptyString: "0",
            property: "pointsEarned",
          },
        ],
        metadata: {
          fraudProbability: fraudProbabilityValue,
        },
        testId: "transactions-table-row",
      };
    });
  }

  return (
    <>
      <Typography
        mb={6}
        data-test-id="single-member-transactions-title"
        className={classes.header}
      >
        {message({ id: "transactions" })}
      </Typography>
      <OLTable
        setColumnOrdering={setColumnOrdering}
        tableName={USER_TRANSACTIONS_LIST_ID}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) =>
                navigate(links.transaction(id as string), {
                  state: { prevPath: links.singleMemberTransactions(memberID) },
                }),
              label: message({ id: "seeDetails" }),
            },
          ],
        }}
        data-test-id="single-member-transactions"
        columns={membersTransactions && headItems}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        rows={membersTransactions && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "header:purchasedAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        pagination={{
          count: calculateCount(
            membersTransactions?.total.filtered,
            membersTransactions?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        onRowClick={(id: string) => {
          navigate(links.transaction(id as string), {
            state: { prevPath: links.singleMemberTransactions(memberID) },
          });
        }}
      />
    </>
  );
}
